import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { ItemType } from '@brickloop/core/schemas';
import { api, qs, type PriceChangeRow } from '../api';
import { canWrite, useAuth } from '../auth';
import { ColorSwatch, ItemImage } from '../components/ItemImage';
import { PriceSuggestionsModal } from '../components/PriceSuggestionsModal';
import { Badge, Button, Card, Empty, ErrorMessage, Notice, PageHeader, Pagination, Spinner, money, number, plural, unitMoney, useConfirm } from '../ui';

type Kind = 'loss' | 'thin' | 'unpriced';
type Tab = Kind | 'changes';

const RULE_NAME: Record<string, string> = { breakeven: 'Break even', floor: 'Floor', cost_plus: 'Cost plus 50%', quick: 'Quick sale', market: 'Market', patient: 'Patient', auto: 'First price', manual: 'By hand' };

interface Row {
  lotId: number;
  itemType: string;
  itemNo: string;
  itemName: string;
  colorId: number;
  colorName: string;
  colorRgb: string | null;
  condition: string;
  qty: number;
  avgCost: string | null;
  price: string | null;
  priceLocked: boolean;
  soldAvg: string | null;
  lossEach: string | null;
  lossTotal: string | null;
}
interface Report {
  kind: Kind;
  counts: Record<Kind, number>;
  total: number;
  rows: Row[];
}
interface PriceNow {
  fetched: number;
  priced: number;
  skipped: number;
  left: number;
  requests: number;
  stoppedBecause?: string;
  message?: string;
}

const TABS: { key: Kind; label: string; note: string }[] = [
  { key: 'loss', label: 'Losing money', note: 'The price is below what the stock cost. Worst first.' },
  { key: 'thin', label: 'Thin margin', note: 'Priced above cost, but under a 20% margin.' },
  { key: 'unpriced', label: 'No price', note: 'In stock with no price. Biggest first.' },
];

/** Where prices need attention: lots selling for less than they cost, on a thin margin, or with no price at all. */
export function Pricing() {
  const { user } = useAuth();
  const writable = canWrite(user);
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const tabKey = (['loss', 'thin', 'unpriced', 'changes'].includes(params.get('kind') ?? '') ? params.get('kind') : 'loss') as Tab;
  const kind: Kind = tabKey === 'changes' ? 'loss' : tabKey;
  const page = Math.max(1, Number(params.get('page')) || 1);
  const [suggesting, setSuggesting] = useState<number | null>(null);

  const changes = useQuery({
    queryKey: ['price-changes', page],
    enabled: tabKey === 'changes',
    queryFn: () => api<{ rows: PriceChangeRow[]; total: number }>(`/api/v1/price-changes${qs({ page })}`),
  });
  const report = useQuery({
    enabled: tabKey !== 'changes',
    queryKey: ['pricing-report', kind, page],
    queryFn: () => api<Report>(`/api/v1/pricing/report${qs({ kind, page })}`),
  });
  const { confirm, dialog } = useConfirm();
  const [costDone, setCostDone] = useState<string | null>(null);
  const toCost = useMutation({
    mutationFn: (lotIds?: number[]) => api<{ result: { changed: number; skippedLocked: number } }>('/api/v1/pricing/set-to-cost', { body: lotIds ? { lotIds } : {} }).then((r) => r.result),
    onSuccess: (r) => {
      setCostDone(`Raised ${r.changed} ${r.changed === 1 ? 'lot' : 'lots'} to their cost.${r.skippedLocked > 0 ? ` ${r.skippedLocked} with a locked price ${r.skippedLocked === 1 ? 'was' : 'were'} left.` : ''}`);
      for (const k of ['pricing-report', 'price-changes', 'lots', 'lot', 'dashboard']) void qc.invalidateQueries({ queryKey: [k] });
    },
  });
  const priceNow = useMutation({
    mutationFn: () => api<PriceNow>('/api/v1/pricing/price-unpriced', { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['pricing-report'] }),
  });
  const r = priceNow.data;
  const tab = TABS.find((t) => t.key === kind)!;
  const data = report.data;

  return (
    <>
      {dialog}
      <PageHeader title="Pricing" subtitle="Lots to look at: the ones losing money, on a thin margin, or with no price." />
      <div className="space-y-4">
        <div role="tablist" className="flex flex-wrap gap-1 border-b border-slate-200">
          {TABS.map((t) => (
            <button
              key={t.key}
              role="tab"
              type="button"
              aria-selected={tabKey === t.key}
              onClick={() => setParams({ kind: t.key })}
              className={`-mb-px border-b-2 px-3 py-2 text-sm ${tabKey === t.key ? 'border-slate-900 font-medium' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
            >
              {t.label} {data && <Badge tone={t.key === 'loss' && data.counts.loss > 0 ? 'red' : 'slate'}>{number(data.counts[t.key])}</Badge>}
            </button>
          ))}
          <button
            role="tab"
            type="button"
            aria-selected={tabKey === 'changes'}
            onClick={() => setParams({ kind: 'changes' })}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${tabKey === 'changes' ? 'border-slate-900 font-medium' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
          >
            Recent price changes
          </button>
        </div>

        {tabKey === 'unpriced' && writable && (
          <Notice tone="blue">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span>
                Gets BrickLink&apos;s figures for lots that have none (two requests each, up to 60 lots at a time), then prices them in line with what they have been selling for. It only reads from BrickLink. The nightly refresh does this too.
              </span>
              <Button disabled={priceNow.isPending || (data?.counts.unpriced ?? 0) === 0} onClick={() => priceNow.mutate()}>
                {priceNow.isPending ? 'Asking BrickLink…' : 'Price them now'}
              </Button>
            </div>
          </Notice>
        )}
        {r && (
          <Notice tone={r.stoppedBecause ? 'amber' : 'green'}>
            Fetched figures for {plural(r.fetched, 'lot')} ({plural(r.requests, 'request')}) and priced {plural(r.priced, 'lot')}.
            {r.skipped > 0 && ` ${plural(r.skipped, 'lot')} had nothing sold lately, so were left.`}
            {r.left > 0 && ` ${plural(r.left, 'lot')} still to do: run it again.`}
            {r.message && ` Stopped: ${r.message}`}
          </Notice>
        )}
        <ErrorMessage error={priceNow.error} />

        {tabKey === 'changes' && (
          <Card>
            <p className="mb-3 text-sm text-slate-600">What pricing rules and automatic pricing changed, and what you set by hand. Newest first. Prices below cost are marked.</p>
            {changes.isPending && <Spinner />}
            {changes.error && <ErrorMessage error={changes.error} />}
            {changes.data && changes.data.rows.length === 0 && <Empty>No price changes yet.</Empty>}
            {changes.data && changes.data.rows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="data w-full min-w-[44rem] text-sm">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Item</th>
                      <th>Colour</th>
                      <th className="text-right">Was</th>
                      <th className="text-right">Now</th>
                      <th>Rule</th>
                      <th>Why</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changes.data.rows.map((c) => (
                      <tr key={c.id}>
                        <td className="whitespace-nowrap">{new Date(c.at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}</td>
                        <td>
                          <Link to={`/lots/${c.lotId}`} className="font-medium hover:underline">
                            {c.itemNo}
                          </Link>
                        </td>
                        <td>{c.color} · {c.condition === 'N' ? 'New' : 'Used'}</td>
                        <td className="text-right tabular-nums">{c.oldPrice ? unitMoney(c.oldPrice) : '—'}</td>
                        <td className="text-right tabular-nums">
                          {unitMoney(c.newPrice)}
                          {c.oldPrice && Number(c.newPrice) !== Number(c.oldPrice) && (
                            <span className={`ml-1 text-xs ${Number(c.newPrice) > Number(c.oldPrice) ? 'text-emerald-700' : 'text-red-700'}`}>{Number(c.newPrice) > Number(c.oldPrice) ? '▲' : '▼'}</span>
                          )}
                          {c.belowCost && <div className="text-xs font-medium text-red-700">below cost</div>}
                        </td>
                        <td>{RULE_NAME[c.rule] ?? c.rule}</td>
                        <td className="max-w-80 text-xs text-slate-600">{c.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {changes.data && <Pagination page={page} total={changes.data.total} pageSize={100} onPage={(p) => setParams({ kind: 'changes', page: String(p) })} />}
          </Card>
        )}

        {tabKey !== 'changes' && (
        <Card>
          <p className="mb-3 text-sm text-slate-600">{tab.note}</p>
          {kind === 'loss' && writable && (data?.counts.loss ?? 0) > 0 && (
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded border border-slate-200 p-3 text-sm">
              <span>
                Rather than sell these at a loss, <strong>raise each to exactly what it cost</strong> (rounded up to the penny): no profit, but no loss. Locked prices are left alone, and each change is in the price history.
              </span>
              <Button
                variant="secondary"
                disabled={toCost.isPending}
                onClick={async () => {
                  if (await confirm(`Raise the price of all ${plural(data?.counts.loss ?? 0, 'lot')} priced under cost to their cost? Their pricing rules are switched off.`, { label: 'Set to cost' })) toCost.mutate(undefined);
                }}
              >
                {toCost.isPending ? 'Working…' : `Set all ${data?.counts.loss ?? 0} to cost`}
              </Button>
            </div>
          )}
          {costDone && <Notice tone="green">{costDone}</Notice>}
          <ErrorMessage error={toCost.error} />
          {report.isPending && <Spinner />}
          {report.error && <ErrorMessage error={report.error} />}
          {data && data.rows.length === 0 && <Empty>Nothing here. {kind === 'loss' ? 'No lot is priced below its cost.' : ''}</Empty>}
          {data && data.rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="data w-full min-w-[48rem] text-sm">
                <thead>
                  <tr>
                    <th className="min-w-14" />
                    <th>Item</th>
                    <th>Colour</th>
                    <th>Cond.</th>
                    <th className="text-right">Qty</th>
                    <th className="text-right">Cost</th>
                    <th className="text-right">Price</th>
                    <th className="text-right">Sells for</th>
                    {kind === 'loss' && <th className="text-right">Loss</th>}
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((x) => (
                    <tr key={x.lotId}>
                      <td className="min-w-14">
                        <ItemImage itemType={x.itemType as ItemType} itemNo={x.itemNo} colorId={x.colorId} />
                      </td>
                      <td>
                        <Link to={`/lots/${x.lotId}`} className="font-medium hover:underline">
                          {x.itemNo}
                        </Link>
                        <div className="max-w-56 truncate text-xs text-slate-500">{x.itemName}</div>
                      </td>
                      <td className="whitespace-nowrap">
                        <ColorSwatch rgb={x.colorRgb} name={x.colorName} />
                      </td>
                      <td>{x.condition === 'N' ? 'New' : 'Used'}</td>
                      <td className="text-right tabular-nums">{number(x.qty)}</td>
                      <td className="text-right tabular-nums">{x.avgCost ? unitMoney(x.avgCost) : '—'}</td>
                      <td className="text-right tabular-nums">
                        {x.price ? unitMoney(x.price) : <span className="text-amber-700">not set</span>}
                        {x.priceLocked && <span title="Locked"> 🔒</span>}
                      </td>
                      <td className="text-right tabular-nums">{x.soldAvg ? unitMoney(x.soldAvg) : '—'}</td>
                      {kind === 'loss' && (
                        <td className="text-right tabular-nums text-red-700">
                          {x.lossTotal ? money(x.lossTotal) : '—'}
                          <div className="text-xs">{x.lossEach ? `${unitMoney(x.lossEach)} each` : ''}</div>
                        </td>
                      )}
                      <td className="text-right">
                        {writable && (
                          <span className="space-x-1 whitespace-nowrap">
                            {kind === 'loss' && (
                              <Button variant="secondary" small disabled={toCost.isPending || x.priceLocked} title={x.priceLocked ? 'The price is locked' : 'Raise the price to what it cost'} onClick={() => toCost.mutate([x.lotId])}>
                                Set to cost
                              </Button>
                            )}
                            <Button variant="secondary" small onClick={() => setSuggesting(x.lotId)}>
                              Suggest
                            </Button>
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data && data.total > 100 && (
            <div className="mt-3 flex items-center justify-between text-sm text-slate-600">
              <span>
                {number((page - 1) * 100 + 1)}–{number(Math.min(page * 100, data.total))} of {number(data.total)}
              </span>
              <div className="flex gap-2">
                <Button variant="secondary" small disabled={page <= 1} onClick={() => setParams({ kind, page: String(page - 1) })}>
                  Previous
                </Button>
                <Button variant="secondary" small disabled={page * 100 >= data.total} onClick={() => setParams({ kind, page: String(page + 1) })}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
        )}
      </div>
      {suggesting !== null && (
        <PriceSuggestionsModal
          lotId={suggesting}
          writable={writable}
          onClose={() => {
            setSuggesting(null);
            void qc.invalidateQueries({ queryKey: ['pricing-report'] });
          }}
        />
      )}
    </>
  );
}
